import React, { useEffect, useRef, useState, useCallback, useId } from "react";
import HoverVideoPlayer from "react-hover-video-player";
import {
  BsFillPlayFill,
  BsHourglassSplit,
  BsPinAngleFill,
  BsVolumeMute,
  BsVolumeUp,
} from "react-icons/bs";

import "../StayUpToDateComponent/StayUpToDate.css";
import "./ShopCardAlignment.css";

import ProductName from "../ProductName/ProductName";
import ProductShopButton from "../ProductName/ProductShopButton";
import IndividualYoutubePostTag from "../StayUpToDateComponent/StayUpToDateIndividuelComponent/IndividualYoutubePostTag";
import ImagePostTag from "./ImagePostTag";
import {
  getMediaSrc,
  redirectToUrl,
  openOutboundUrl,
} from "../../Utils/StayUpToDate/RenderComponent";
import { resolveFeedCardImage } from "../../Utils/media/feedCardImage";
import { normalizeSoundValue } from "../../Utils/helpers/carouselHelper";
import {
  DUMMY_VIDEO_POSTER,
  isDefiniteVideoPostType,
  listIndexedVideoPosterUrls,
  listVideoPosterUrls,
  resolvePlayableVideoUrl,
} from "../../Utils/media/feedMedia";
import { hasShoppableProductStill } from "../../Utils/productImage/productImageDisplay";
import {
  claimExclusiveHoverVideo,
  registerExclusiveHoverVideo,
  releaseExclusiveHoverVideo,
} from "../../Utils/exclusiveHoverVideo";
import {
  useTemplateMediaIndex,
  useTemplateMediaReveal,
} from "../../Screens/DashboardView/MasterTemplateEditor/useTemplateMediaReveal";
import "../../Screens/DashboardView/MasterTemplateEditor/templateMediaReveal.css";

/**
 * Landing carousel architecture:
 * - Card shell (wrapper + title/button) always mounts so CSS/scrollWidth stay intact.
 * - HoverVideoPlayer mounts only when `enableInteractiveMedia` is true (near the
 *   horizontal scroll window). Otherwise a same-class poster image is shown.
 */
const VideoPostTag = (props: any) => {
  const {
    post,
    title,
    handleRedirect,
    isLinks,
    brand_color,
    isDashboard,
    isYoutube,
    isHasMediaUrl,
    thumbnail,
    mediaIndex,
    productName,
    type,
    isCarousel,
    media_url,
    contentShape,
    isSoundOn: isSoundOnProp,
    from,
    designTokens,
    pinnedPostIds,
    enableInteractiveMedia = true,
    disableHoverPlayback = false,
    source,
  } = props;
  const redirectUrl: any = redirectToUrl(post, isLinks);
  const permalinkUrl: any = post?.permalink || null;

  const isSoundOn = normalizeSoundValue(isSoundOnProp, true);

  const isYoutubePost =
    Boolean(isYoutube) ||
    String(post?.platform || "").toLowerCase() === "youtube" ||
    /(?:youtube\.com|youtu\.be)\//i.test(String(post?.permalink || ""));

  // Never use the hover-video shell for YouTube — thumb URLs often contain
  // "video" and get mis-routed here. Website LiB uses the same cover tile as IG.
  if (isYoutubePost && !post?.product_image) {
    if (from === "landing-page" || from === "website-template") {
      return (
        <ImagePostTag
          data={post}
          handleRedirect={handleRedirect}
          title={title}
          brand_color={brand_color}
          isLinks={isLinks}
          isDashboard={isDashboard}
          type={type}
          contentShape={contentShape}
          from={from}
          pinnedPostIds={pinnedPostIds}
          designTokens={designTokens}
          source={source}
          isYoutube={false}
        />
      );
    }
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      const targetUrl = post?.permalink;
      if (!targetUrl) return;
      handleRedirect?.(
        post?.post_id,
        post?.media_url,
        post?.platform,
        post?.product_name,
        title,
        isLinks,
        from
      );
      openOutboundUrl(targetUrl);
    };
    return (
      <IndividualYoutubePostTag
        link={post}
        handleRedirect={handleRedirect}
        bgColor={brand_color}
        contentShape={contentShape}
        handleClick={handleClick}
        redirectUrl={post?.permalink || undefined}
        source={source}
        pinnedPostIds={pinnedPostIds}
        isDashboard={isDashboard}
        from={from}
        designTokens={designTokens}
      />
    );
  }

  // Only swap to a product still for non-video posts. REELS/VIDEO/TIKTOK must
  // keep the video player even when isLinks + product_image are set (content
  // day-windows pass isLinks for Shop buttons).
  if (
    !props.suppressProductStill &&
    hasShoppableProductStill(post, isLinks) &&
    !isDefiniteVideoPostType(String(post?.type || type || ""), post?.platform)
  ) {
    return (
      <ImagePostTag
        data={post}
        handleRedirect={handleRedirect}
        title={title}
        brand_color={brand_color}
        isLinks={isLinks}
        isDashboard={isDashboard}
        type={type}
        contentShape={contentShape}
        from={from}
        pinnedPostIds={pinnedPostIds}
        designTokens={designTokens}
        source={source}
      />
    );
  }

  const openUrlWithTracking = (targetUrl: string | null | undefined) => {
    if (!targetUrl) return;

    handleRedirect(
      post?.post_id,
      getMediaSrc(undefined, post, false, post?.media_url) ||
        post?.thumbnail ||
        post?.media_url,
      post?.platform,
      post?.product_name,
      title,
      isLinks,
      from
    );

    openOutboundUrl(targetUrl);
  };

  return (
    <>
      <VideoTag
        media_url={media_url || post?.media_url}
        post={post}
        thumbnail={thumbnail}
        mediaIndex={mediaIndex}
        brand_color={brand_color}
        isYoutube={isYoutube}
        handleRedirect={handleRedirect}
        title={title}
        isLinks={isLinks}
        isHasMediaUrl={isHasMediaUrl}
        productName={productName}
        type={type}
        contentShape={contentShape}
        isSoundOn={isSoundOn}
        isCarousel={isCarousel}
        from={from}
        pinnedPostIds={pinnedPostIds}
        permalinkUrl={permalinkUrl}
        redirectUrl={redirectUrl}
        designTokens={designTokens}
        isDashboard={isDashboard}
        enableInteractiveMedia={enableInteractiveMedia}
        disableHoverPlayback={disableHoverPlayback}
        source={source}
      />
    </>
  );
};

const VideoTag = (props: any) => {
  const {
    media_url,
    brand_color,
    post,
    isYoutube,
    thumbnail,
    mediaIndex,
    title,
    isLinks,
    isHasMediaUrl,
    productName,
    type,
    contentShape,
    isSoundOn,
    isCarousel,
    from,
    designTokens,
    pinnedPostIds,
    permalinkUrl,
    redirectUrl,
    handleRedirect,
    isDashboard,
    enableInteractiveMedia = true,
    disableHoverPlayback = false,
    source,
  } = props;
  const videoRef: any = useRef();
  const hoverTargetRef = useRef<HTMLDivElement>(null);
  const reactId = useId();
  const playerId = `${post?.post_id ?? "video"}-${media_url ?? ""}-${reactId}`;
  const playableSrc =
    resolvePlayableVideoUrl({
      mediaUrl: media_url || post?.media_url,
      storageUrl: post?.storage_url,
      type: type || post?.type,
      platform: post?.platform,
    }) ||
    media_url ||
    post?.media_url;
  const [hover, setHover] = useState(false);
  // Mute preference lives on the shell so it survives player remounts.
  const [isMuted, setIsMuted] = useState(true);
  const [customPause, setCustomPause] = useState(false);
  const [showClickablePlayBtn, setShowClickablePlayBtn] = useState(false);
  const isTemplateSite = from === "website-template";
  const ctxIndex = useTemplateMediaIndex();

  // When scroll ends with the cursor already over this card, mouseenter does
  // not fire — use focused to nudge react-hover-video-player to play.
  const [forceFocused, setForceFocused] = useState(false);
  const isPinned = Array.isArray(pinnedPostIds)
    ? pinnedPostIds.includes(post?.post_id)
    : false;
  // Carousel albums often lack thumbnail_url; still slides live in media_url.
  const posterCandidates = (
    typeof mediaIndex === "number"
      ? listIndexedVideoPosterUrls(post, mediaIndex)
      : listVideoPosterUrls(post, thumbnail)
  ).map((url) => resolveFeedCardImage(url).src);
  const thumbnailSrc = posterCandidates[0] || DUMMY_VIDEO_POSTER;
  const [posterSrc, setPosterSrc] = useState(thumbnailSrc);
  useEffect(() => {
    setPosterSrc(thumbnailSrc);
  }, [thumbnailSrc]);

  const {
    setRef: setRevealRef,
    activeSrc: revealPosterSrc,
    onLoad: onRevealLoad,
    onError: onRevealError,
    wrapperClassName: revealClass,
    revealed: mediaReady,
  } = useTemplateMediaReveal({
    enabled: isTemplateSite,
    src: posterSrc,
    index: typeof mediaIndex === "number" ? mediaIndex : ctxIndex ?? 0,
    priority: typeof mediaIndex === "number" ? mediaIndex < 6 : false,
  });
  const displayPosterSrc = isTemplateSite ? revealPosterSrc : posterSrc;

  const onPosterError = () => {
    onRevealError();
    const currentIdx = posterCandidates.indexOf(posterSrc);
    const next =
      (currentIdx >= 0 ? posterCandidates[currentIdx + 1] : posterCandidates[0]) ||
      DUMMY_VIDEO_POSTER;
    if (next && next !== posterSrc) {
      setPosterSrc(next);
    } else if (posterSrc !== DUMMY_VIDEO_POSTER) {
      setPosterSrc(DUMMY_VIDEO_POSTER);
    }
  };

  const getWrapperClassName = () => {
    const baseClass = "creator-feed-image-wrapper-links";
    const classes = [baseClass];

    if (contentShape == "sq") classes.push("square-wrapper");
    else if (isYoutube) classes.push("youtube-feed-wrapper");
    if (isTemplateSite && revealClass) classes.push(revealClass);
    return classes.join(" ");
  };

  const markTemplateThumbReady = () => {
    onRevealLoad();
  };

  const getVideoClassName = () => {
    const baseClass = "creator-feed-card-links-image";
    const classes = [baseClass];

    if (contentShape == "sq") classes.push("square-video");
    else if (isYoutube) classes.push("youtube-feed-height");

    return classes.join(" ");
  };

  const forceHoverEnd = useCallback(() => {
    hoverTargetRef.current?.dispatchEvent(new Event("hvp:hoverEnd"));
    setForceFocused(false);
    setHover(false);
    setIsMuted(true);
    const video = videoRef.current as HTMLVideoElement | undefined;
    if (video && !video.paused) {
      video.pause();
    }
  }, []);

  const startExclusiveHoverPlay = useCallback(() => {
    claimExclusiveHoverVideo(playerId);
    setHover(true);
  }, [playerId]);

  // Register so other cards can pause/unload this player when they take over.
  useEffect(() => {
    if (!enableInteractiveMedia) return;
    return registerExclusiveHoverVideo(playerId, () => {
      // Claimer already owns activePlayerId — only stop this player locally.
      forceHoverEnd();
    });
  }, [enableInteractiveMedia, playerId, forceHoverEnd]);

  useEffect(() => {
    if (
      navigator.userAgent.search("Safari") >= 0 &&
      navigator.userAgent.search("Chrome") < 0
    ) {
      setShowClickablePlayBtn(true);
    }
  }, []);

  useEffect(() => {
    if (enableInteractiveMedia && videoRef.current) {
      videoRef.current.playsInline = true;
    }
  }, [enableInteractiveMedia]);

  useEffect(() => {
    if (!enableInteractiveMedia) {
      setForceFocused(false);
      setHover(false);
      releaseExclusiveHoverVideo(playerId);
      return;
    }

    if (disableHoverPlayback) {
      forceHoverEnd();
      releaseExclusiveHoverVideo(playerId);
      return;
    }

    if (hoverTargetRef.current?.matches?.(":hover")) {
      claimExclusiveHoverVideo(playerId);
      hoverTargetRef.current.dispatchEvent(new Event("hvp:hoverStart"));
      setForceFocused(true);
      setHover(true);
    }
  }, [disableHoverPlayback, enableInteractiveMedia, forceHoverEnd, playerId]);

  const OverlayStatus = ({
    hover,
  }: {
    hover: boolean;
    showClickablePlayBtn: boolean;
  }) => {
    if (isTemplateSite && !mediaReady) return null;
    return (
      <>
        {!hover && (
          <BsFillPlayFill
            className={"creator-feed-card-play-btn"}
            style={{ color: "#fff", zIndex: 3, pointerEvents: "none" }}
          />
        )}
      </>
    );
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    startExclusiveHoverPlay();
    hoverTargetRef.current?.dispatchEvent(new Event("hvp:hoverStart"));
    // Mobile browsers pause if unmute lands outside the tap gesture.
    const video = videoRef.current as HTMLVideoElement | undefined;
    if (!video) return;
    video.muted = nextMuted;
    if (video.paused) video.play().catch(() => {});
  };

  const renderPinnedBadge = () =>
    isPinned && source !== "edit" ? (
      <div
        className="pinned-badge"
        aria-label="Pinned post"
        style={{ right: "4px" }}
      >
        <BsPinAngleFill />
      </div>
    ) : null;

  const openUrlWithTracking = (targetUrl: string | null | undefined) => {
    if (!targetUrl) return;

    handleRedirect(
      post?.post_id,
      getMediaSrc(undefined, post, false, post?.media_url) ||
        post?.thumbnail ||
        post?.media_url,
      post?.platform,
      post?.product_name,
      title,
      isLinks,
      from
    );

    openOutboundUrl(targetUrl);
  };

  const trackShopClick = () => {
    // Shop CTA must always track as shop — never inherit carousel isLinks=false.
    handleRedirect(
      post?.post_id,
      getMediaSrc(undefined, post, false, post?.media_url) ||
        post?.thumbnail ||
        post?.media_url,
      post?.platform,
      post?.product_name,
      title,
      true,
      from
    );
  };

  const renderPosterImg = (loading: "eager" | "lazy") => (
    <img
      src={posterSrc}
      alt=""
      className={getVideoClassName()}
      style={{ display: "block", objectFit: "cover" }}
      loading={loading}
      decoding={loading === "eager" ? "sync" : "async"}
      onError={onPosterError}
    />
  );

  const renderPoster = () => (
    <div
      style={{ position: "relative", display: "block" }}
      className={getVideoClassName()}
    >
      {renderPosterImg("lazy")}
      <OverlayStatus hover={false} showClickablePlayBtn={showClickablePlayBtn} />
    </div>
  );

  const setHoverAndRevealRef = (node: HTMLDivElement | null) => {
    hoverTargetRef.current = node;
    setRevealRef(node);
  };

  const canShowMedia = Boolean(playableSrc) && (!isTemplateSite || Boolean(displayPosterSrc));

  const mediaBody = !Boolean(playableSrc) ? (
    isTemplateSite ? (
      <div className="mte-tpl-embed-media-placeholder" aria-hidden />
    ) : (
      <div className={`creator-feed-media-placeholder ${getVideoClassName()}`}>
        <BsHourglassSplit className="hourglassSplit" />
        <p>Media unavailable</p>
      </div>
    )
  ) : !canShowMedia ? null : !enableInteractiveMedia ? (
    renderPoster()
  ) : (
    <HoverVideoPlayer
      videoRef={videoRef}
      videoSrc={playableSrc}
      muted={isSoundOn ? isMuted : true}
      className={getVideoClassName()}
      // Size to the paused overlay (CSS-locked card size) so videos match image posts.
      sizingMode="overlay"
      style={{
        display: "block",
        border: "none",
        overflow: "hidden",
      }}
      preload="none"
      contextMenu="return false;"
      hoverTarget={hoverTargetRef}
      focused={forceFocused}
      pausedOverlayWrapperStyle={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
      loadingOverlayWrapperStyle={{
        pointerEvents: "none",
      }}
      hoverOverlayWrapperStyle={{
        pointerEvents: "none",
      }}
      hoverOverlay={
        isSoundOn && (
          <div
            className="sound-icon"
            style={{ pointerEvents: "auto" }}
            onClick={toggleMute}
          >
            {isMuted ? (
              <BsVolumeMute size={24} color="white" />
            ) : (
              <BsVolumeUp size={24} color="white" />
            )}
          </div>
        )
      }
      pausedOverlay={
        <div
          style={{ position: "relative", display: "block" }}
          className={getVideoClassName()}
        >
          {renderPosterImg("eager")}
          <OverlayStatus
            hover={hover}
            showClickablePlayBtn={showClickablePlayBtn}
          />
        </div>
      }
      loadingOverlay={renderPosterImg("eager")}
      unloadVideoOnPaused
      onHoverStart={() => {
        if (disableHoverPlayback) {
          forceHoverEnd();
          releaseExclusiveHoverVideo(playerId);
          return;
        }
        startExclusiveHoverPlay();
      }}
      onHoverEnd={() => {
        setHover(false);
        setCustomPause(false);
        setForceFocused(false);
        setIsMuted(true);
        releaseExclusiveHoverVideo(playerId);
      }}
      disableDefaultEventHandling={customPause}
    />
  );

  // Match ImagePostTag: media shell + shop/name as siblings so Shop buttons share a baseline.
  // Do not set display:block here — ShopCardAlignment.css uses flex + gap for media→Shop.
  return (
    <div
      className="creator-post-stack"
      style={{ position: "relative" }}
    >
      <div
        ref={setHoverAndRevealRef}
        className={getWrapperClassName()}
        style={{ position: "relative" }}
        onClick={() => openUrlWithTracking(permalinkUrl)}
      >
        {renderPinnedBadge()}
        {isTemplateSite && displayPosterSrc ? (
          <img
            src={displayPosterSrc}
            alt=""
            aria-hidden
            className="mte-tpl-media-reveal-sensor"
            decoding="async"
            onLoad={markTemplateThumbReady}
            onError={onPosterError}
          />
        ) : null}
        {isTemplateSite ? (
          <div className="mte-tpl-reveal-media-slot">{mediaBody}</div>
        ) : (
          mediaBody
        )}
      </div>
      {!isCarousel && (
        <>
          <ProductName type={type} post={post} designTokens={designTokens} />
          <ProductShopButton
            isLinks={isLinks}
            IsIndividual={false}
            type={type}
            post={post}
            isDashboard={isDashboard}
            onBeforeNavigate={trackShopClick}
            designTokens={designTokens}
          />
        </>
      )}
    </div>
  );
};

export default VideoPostTag;
